import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";

gsap.registerPlugin(ScrollTrigger);

const bodyTag = document.querySelector('body');

export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

export const initAnimations = () => {
  // Desktop breakpoint (≥1280px)
  const DESKTOP_QUERY = "(min-width: 1024px)";
  const mm = gsap.matchMedia();

  // Common settings
  const settings = { duration: 1 };

  // Helper: attach ScrollTrigger to a timeline
  const initScrollTrigger = (tl, el, start = "top bottom=20%") => {
    ScrollTrigger.create({
      animation: tl,
      trigger: el,
      start: () => start,
    });
  };

  // Helper: default "rise up" animation tuple
  const getDefaultAnim = (from, to) => ([
    { opacity: 0, ...from },
    { opacity: 1, duration: settings.duration, stagger: 0.4, ease: "power1.out", ...to },
  ]);

  // Selectors we own (used to safely kill only our triggers)
  const OWN_SELECTORS = [
    "[data-rise-up]",
  ].join(", ");

  // Mobile behavior: kill triggers and set elements to final state
  const disableAnimsOnMobile = () => {
    // Kill only our ScrollTriggers
    ScrollTrigger.getAll().forEach(st => {
      if (!st.trigger) return;
      if (st.trigger.matches(OWN_SELECTORS) || st.trigger.closest(OWN_SELECTORS)) st.kill();
    });

    // Set final styles (as if animations already completed)
    document.querySelectorAll(OWN_SELECTORS).forEach(el => {
      if (el.matches('[data-rise-list]')) {
        gsap.set(el.querySelectorAll('[data-rise-item]'), { clearProps: "all", x: 0, y: 0, opacity: 1 });
      } else if (el.matches('[data-left-list]')) {
        gsap.set(el.querySelectorAll('[data-left-item]'), { clearProps: "all", x: 0, y: 0, opacity: 1 });
      } else {
        gsap.set(el, { clearProps: "all", x: 0, y: 0, opacity: 1 });
      }
    });
  };

  // Desktop-only init: all your timelines wrapped in a gsap.context
  mm.add(DESKTOP_QUERY, () => {
    const ctx = gsap.context(() => {
      // === riseUp ===
      const riseUp = document.querySelectorAll("[data-rise-up]");
      riseUp.forEach((el) => {
        const delay = el.getAttribute('data-delay') || 0;
        const start = el.getAttribute('data-start') || 'top bottom-=10%';
        const tl = gsap.fromTo(el, ...getDefaultAnim({}, { delay, duration: settings.duration }));
        if (el.closest('[data-rise-up-group]')) {
          initScrollTrigger(tl, el.closest('[data-rise-up-group]'));
        } else {
          initScrollTrigger(tl, el, start);
        }
      });
    });

    // Cleanup when leaving the desktop breakpoint
    return () => ctx.revert();
  });

  // Mobile & tablet (<1024px): ensure animations are disabled and elements are "final"
  mm.add("(max-width: 1023px)", () => {
    disableAnimsOnMobile();
  });
};

const splitTextAnim = document.querySelectorAll('[data-split-wrap]');

let splitInstances = [];

function initSplitText() {
  // Revert previous SplitText instances
  splitInstances.forEach(split => split?.revert?.());
  splitInstances = [];

  // Restore original markup before rebuilding
  document.querySelectorAll('[data-split-text]').forEach(item => {
    if (item.dataset.originalHtml) {
      item.innerHTML = item.dataset.originalHtml;
    }
  });

  splitTextAnim.forEach((el) => {
    const items = el.querySelectorAll('[data-split-text]');

    items.forEach((item) => {
      if (!item.dataset.originalHtml) {
        item.dataset.originalHtml = item.innerHTML;
      }

      el.classList.add('visible');

      const split = new SplitText(item, {
        type: 'words',
        wordsClass: 'word',
        tag: 'span',
        aria: false,
      });

      splitInstances.push(split);

      // Build animation targets in DOM order
      const targets = [];

      item.childNodes.forEach((node) => {
        // SplitText wraps words in spans
        if ( node.nodeType === 1 && node.classList.contains('word') ) {
          targets.push(node);
        }

        if ( node.nodeType === 1 && node.hasAttribute('data-split-icon') ) {
          targets.push(node);
        }
      });

      gsap.fromTo( targets,
        {
          opacity: 0,
          y: 20,
        },
        {
          opacity: 1,
          duration: 0.2,
          stagger: 0.2,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: bodyTag,
            start: 'top bottom',
            markers: false,
          },
        }
      );
    });
  });
}

// init
initSplitText();
